// `dayLogs`: Swift `DayLog` / `TrackPoint` (GroundTeam/Models/Engagement.swift): one rep's GPS breadcrumbs, mileage and
// working time for a day. Read-only on the web (WEB_PLAN §2.3); live rep positions are derived from the newest point.
//
// Decoding template (GroundTeam/Sync/ResilientDecoding.swift:128): `DayLog(userId: UUID(), date: epoch,
// points: [TrackPoint(coordinate: (0, 0), timestamp: epoch)])`. Each stored point is merged over that template point
// (a missing coordinate half reads 0, a missing timestamp the epoch), a missing `userId` reads as a random UUID and a
// missing `date` as the epoch, and a payload with no `points` key reads as that single template point. All mirrored.

import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import { TEMPLATED_COORDINATE, type CoordinatePayload } from './common';
import {
  DATE,
  DOUBLE,
  EPOCH,
  UUID,
  arrayOf,
  createModel,
  decodeModel,
  field,
  objectOf,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface TrackPointPayload extends Passthrough {
  coordinate: CoordinatePayload;
  timestamp: string;
  /** Double, meters per second. */
  speedMps: number;
}

export const TRACK_POINT_KEYS = ['coordinate', 'timestamp', 'speedMps'] as const satisfies readonly (keyof Known<
  TrackPointPayload
>)[];

export const TRACK_POINT_FIELDS: FieldMap<TrackPointPayload> = {
  coordinate: field.req(TEMPLATED_COORDINATE, () => ({ latitude: 0, longitude: 0 })),
  timestamp: field.req(DATE, () => EPOCH),
  speedMps: field.req(DOUBLE, () => 0),
};

export const TRACK_POINT = objectOf<TrackPointPayload>('TrackPoint', TRACK_POINT_FIELDS);

/** Reads one breadcrumb as an element of `points` (merged over the template point). */
export function decodeTrackPoint(raw: JsonObject): Decoded<TrackPointPayload> {
  return decodeModel(TRACK_POINT_FIELDS, raw, { hasId: false });
}

export type TrackPointInit = Partial<Known<TrackPointPayload>> & Pick<Known<TrackPointPayload>, 'coordinate' | 'timestamp'>;

export function newTrackPoint(init: TrackPointInit): TrackPointPayload {
  return createModel(TRACK_POINT_FIELDS, { speedMps: 0 }, init, 'point');
}

export interface DayLogPayload extends Passthrough {
  id: string;
  userId: string;
  /** The day (the rep's local midnight, as a payload date). */
  date: string;
  points: TrackPointPayload[];
  startedAt?: string;
  endedAt?: string;
  totalMeters: number;
  firstActivityAt?: string;
  lastActivityAt?: string;
}

export const DAY_LOG_KEYS = [
  'id',
  'userId',
  'date',
  'points',
  'startedAt',
  'endedAt',
  'totalMeters',
  'firstActivityAt',
  'lastActivityAt',
] as const satisfies readonly (keyof Known<DayLogPayload>)[];

export const DAY_LOG_FIELDS: FieldMap<DayLogPayload> = {
  id: field.strict(UUID),
  userId: field.req(UUID, newId),
  date: field.req(DATE, () => EPOCH),
  points: field.req(arrayOf(TRACK_POINT), () => [
    { coordinate: { latitude: 0, longitude: 0 }, timestamp: EPOCH, speedMps: 0 },
  ]),
  startedAt: field.opt(DATE),
  endedAt: field.opt(DATE),
  totalMeters: field.req(DOUBLE, () => 0),
  firstActivityAt: field.opt(DATE),
  lastActivityAt: field.opt(DATE),
};

export function decodeDayLog(raw: JsonObject, envelopeId?: string): Decoded<DayLogPayload> {
  return decodeModel(DAY_LOG_FIELDS, raw, {}, envelopeId);
}

/** Swift's memberwise init needs `userId` and `date`. */
export type DayLogInit = Omit<Partial<Known<DayLogPayload>>, 'points'> &
  Pick<Known<DayLogPayload>, 'userId' | 'date'> & { points?: TrackPointInit[] };

export function newDayLog(init: DayLogInit): DayLogPayload {
  const { points, ...rest } = init;
  const defaults = {
    id: newId(),
    points: (points ?? []).map((p) => newTrackPoint(p)),
    totalMeters: 0,
  };
  return createModel(DAY_LOG_FIELDS, defaults, rest, 'dayLogs');
}
